import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { patch } from "@rails/request.js";
import { AnimatePresence, motion } from "framer-motion";
import { RiAsterisk } from "react-icons/ri";
import { SiX, SiLinkedin, SiDiscord } from "react-icons/si";
import { FiAlignCenter, FiMic, FiVideo } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import TipTapArea from "./../capform/TipTapArea";
import { Toasts, useToasts } from "./../shared/Toast";
import { ImageUploader } from "./../shared/ImageUploader";
import { VideoUploader } from "./../shared/VideoUploader";
import { AudioUploader } from "./../shared/AudioUploader";

const SOURCES_DIRECT = [
  { key: "text",  label: "Text",  Icon: FiAlignCenter },
  { key: "video", label: "Video", Icon: FiVideo },
  { key: "audio", label: "Audio", Icon: FiMic },
];

const SOURCES_SOCIAL = [
  { key: "Twitter/X", label: "Twitter / X", Icon: SiX },
  { key: "Discord",   label: "Discord",     Icon: SiDiscord },
  { key: "LinkedIn",  label: "LinkedIn",    Icon: SiLinkedin },
];

const ProofEdit = ({ kind, project_id, initProof }) => {
  const [proof, setProof] = useState(initProof.proof);
  const { toasts, setToasts, addToast } = useToasts();
  const {
    formState,
    register,
    watch,
    reset,
    handleSubmit,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      source_platform: proof.source_platform,
      headline: proof.headline,
      body: proof.body,
      name: proof.name,
      title: proof.contact_title,
      avatar: proof.avatar || "https://images.shosay.com/placeholder_avatar3.webp",
      company_logo: proof.company_logo || "https://images.shosay.com/placeholder_avatar2.webp",
      email: proof.email,
      video_url: proof.video_url,
      audio_url: proof.audio_url,
      given_at_utc: proof.given_at_utc_db ? new Date(proof.given_at_utc_db) : new Date(),
      source_url: proof.source_url,
      kind: proof.kind,
      company_name: proof.contact_company_name,
      company_team: proof.contact_company_team,
    },
  });

  const { errors, isLoading } = formState;

  const handleUpdate = (key, val) => {
    setValue(key, val, { shouldDirty: true, shouldValidate: true });
  };
  const handleAvatarChange = (url) => handleUpdate("avatar", url);
  const handleCompanyLogoChange = (url) => handleUpdate("company_logo", url);
  const handleVideoUrlChange = (url) => handleUpdate("video_url", url);
  const handleAudioUrlChange = (url) => handleUpdate("audio_url", url);

  const onSubmit = async (data) => {
    const response = await patch(`/api/v1/proofs/${initProof.proof.id}`, {
      body: {
        proof: {
          headline: data.headline,
          body: data.body,
          given_at_utc: data.given_at_utc,
          video_url: data.video_url,
          audio_url: data.audio_url,
          source_tagline: data.source_tagline,
          source_platform: data.source_platform,
          source_url: data.source_url,
        },
        contact: {
          name: data.name,
          email: data.email,
          title: data.title,
          avatar: data.avatar,
          company_name: data.company_name,
          company_team: data.company_team,
          company_logo: data.company_logo,
        },
      },
      contentType: "application/json",
      responseKind: "json",
    });
    if (response.ok) {
      const json = await response.json;
      reset(json.proof);
      setProof(json.proof);
      addToast("success", "Saved!");
    } else {
      addToast("error", "Error saving form");
    }
  };

  const selectedP = watch("source_platform");
  const body = getValues().body;
  const avatar = getValues().avatar;
  const company_logo = getValues().company_logo;
  const given_at_utc = getValues().given_at_utc;
  const video_url = getValues().video_url;
  const audio_url = getValues().audio_url;

  return (
    <div className="zz-panel p-5 sm:p-8">
      <div className="zz-tape" style={{ background: "rgba(127,206,251,0.85)" }}></div>
      <Toasts toasts={toasts} setToasts={setToasts} />

      {!selectedP && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <p className="zz-hand text-2xl text-ssblue leading-none">first —</p>
            <h2 className="zz-display text-2xl sm:text-3xl font-black leading-tight">
              Where is your <span className="zz-highlight yellow">{kind}</span> from?
            </h2>

            <p className="zz-section">offline, emails, random places</p>
            <div className="flex flex-wrap gap-3">
              {SOURCES_DIRECT.map((s) => (
                <SourceBtn key={s.key} source={s} handleUpdate={handleUpdate} kind={kind} label={`${s.label} ${kind}`} />
              ))}
            </div>

            <p className="zz-section blue">social platforms</p>
            <div className="flex flex-wrap gap-3">
              {SOURCES_SOCIAL.map((s) => (
                <SourceBtn key={s.key} source={s} handleUpdate={handleUpdate} kind={kind} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {selectedP && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <p className="zz-hand text-2xl text-ssblue leading-none">source —</p>
                <p className="zz-display text-2xl sm:text-3xl font-black leading-tight capitalize">{selectedP}</p>
              </div>
              <button
                type="button"
                onClick={() => handleUpdate("source_platform", null)}
                className="zz-link muted text-sm"
              >
                ← change
              </button>
            </div>

            <p className="zz-section">the {kind} itself</p>
            <div className="space-y-4">
              <div>
                <label className="zz-label">
                  Headline
                  <span className="ml-1 text-ssred">*</span>
                  {errors.headline && <span className="ml-2 text-ssred normal-case tracking-normal text-xs font-normal">required</span>}
                </label>
                <input
                  type="text"
                  {...register("headline", { required: true })}
                  autoFocus
                  placeholder="Give it a short, punchy headline"
                  spellCheck="false"
                  className="zz-input"
                />
              </div>

              {selectedP === "video" && (
                <div>
                  <label className="zz-label">Video file</label>
                  <VideoUploader setUrl={handleVideoUrlChange} initialUrl={video_url} />
                </div>
              )}
              {selectedP === "audio" && (
                <div>
                  <label className="zz-label">Audio file</label>
                  <AudioUploader setUrl={handleAudioUrlChange} initialUrl={audio_url} />
                </div>
              )}
              {selectedP !== "video" && selectedP !== "audio" && (
                <div>
                  <label className="zz-label">
                    Body
                    <span className="ml-1 text-ssred">*</span>
                    {errors.body && <span className="ml-2 text-ssred normal-case tracking-normal text-xs font-normal">required</span>}
                  </label>
                  <div className="border-[3px] border-black rounded-lg overflow-hidden">
                    <TipTapArea html={body} updateHtml={(val) => handleUpdate("body", val)} />
                  </div>
                </div>
              )}
            </div>

            <div className="zz-divider"></div>

            <p className="zz-section green">who gave it to you?</p>

            <div className="grid gap-4 grid-cols-1 sm:grid-cols-5 mb-4">
              <div className="sm:col-span-2">
                <label className="zz-label">Avatar</label>
                <ImageUploader initialImageUrl={avatar} setUrl={handleAvatarChange} />
              </div>
              <div className="sm:col-span-3 flex flex-col gap-3">
                <div>
                  <label className="zz-label">
                    Name
                    <span className="ml-1 text-ssred">*</span>
                    {errors.name && <span className="ml-2 text-ssred normal-case tracking-normal text-xs font-normal">required</span>}
                  </label>
                  <input
                    type="text"
                    {...register("name", { required: true })}
                    placeholder="Their name"
                    spellCheck="false"
                    className="zz-input"
                  />
                </div>
                <div>
                  <label className="zz-label">
                    Email
                    {errors.email && <span className="ml-2 text-ssred normal-case tracking-normal text-xs font-normal">{errors.email.message}</span>}
                  </label>
                  <input
                    type="email"
                    placeholder="hello@example.com"
                    {...register("email", {
                      required: false,
                      pattern: { value: /\S+@\S+\.\S+/, message: "Bad email format" },
                    })}
                    spellCheck="false"
                    className="zz-input"
                  />
                </div>
              </div>
            </div>

            <p className="zz-section orange">their company (if any)</p>
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-5 mb-4">
              <div className="sm:col-span-2">
                <label className="zz-label">Company logo</label>
                <ImageUploader initialImageUrl={company_logo} setUrl={handleCompanyLogoChange} />
              </div>
              <div className="sm:col-span-3 flex flex-col gap-3">
                <div>
                  <label className="zz-label">Job title</label>
                  <input type="text" placeholder="Designer" {...register("title")} spellCheck="false" className="zz-input" />
                </div>
                <div>
                  <label className="zz-label">Company</label>
                  <input type="text" placeholder="Acme Bricks Inc" {...register("company_name")} spellCheck="false" className="zz-input" />
                </div>
                <div>
                  <label className="zz-label">Team</label>
                  <input type="text" placeholder="Design" {...register("company_team")} spellCheck="false" className="zz-input" />
                </div>
              </div>
            </div>

            <p className="zz-section">other bits</p>
            <div className="grid gap-4 sm:grid-cols-2 mb-4">
              <div>
                <label className="zz-label">
                  When was it?
                  <span className="ml-1 text-ssred">*</span>
                </label>
                <DatePicker
                  className="zz-input"
                  selected={given_at_utc}
                  withPortal
                  onChange={(date) => handleUpdate("given_at_utc", date)}
                />
              </div>
              <div>
                <label className="zz-label">URL it came from</label>
                <input
                  type="text"
                  {...register("source_url")}
                  placeholder="https://example.com"
                  spellCheck="false"
                  className="zz-input"
                />
              </div>
            </div>

            <div aria-hidden="true" className="h-12 sm:h-4"></div>
            <div className="flex items-center justify-center sm:justify-end gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading}
                className={`zz-btn blue ${isLoading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {isLoading ? "saving…" : `save ${kind} ✓`}
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

const SourceBtn = ({ source, handleUpdate, kind, label }) => {
  const Icon = source.Icon;
  const onClick = () => handleUpdate("source_platform", source.key);
  return (
    <button type="button" onClick={onClick} className="zz-btn sm blue">
      <span className="flex items-center gap-2">
        <Icon className="text-base" /> {label || source.label}
      </span>
    </button>
  );
};

export default ProofEdit;
